import React, { useState, useEffect } from 'react';
import { formatLibelleForDisplay } from '../utils/libelleFormatter';
import CgmModulePageLayout from './layout/CgmModulePageLayout';
import { getActiveCaisseYear, buildCaisseYearOptions } from '../utils/caisseActiveYear';
import { authJsonHeaders } from '../utils/authHeaders';
import { useAuth } from '../contexts/AuthContext';
import './CgmFilterToolbar.css';
import './CgmModulePage.css';
import './NHPage.css';

/** Crédit client = HONORAIRES REÇU CABINET (libellé normalisé). */
function isHonoraireRecuCabinet(libelle) {
  const u = String(libelle || '')
    .toUpperCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '');
  return u.includes('HONORAIRES RECU CABINET') || u.includes('HONORAIRE RECU CABINET');
}

export default function AllHonorairesPage() {
  const { activeCabinet } = useAuth();
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState([]);
  const [error, setError] = useState('');
  const [total, setTotal] = useState(0);
  const [tab, setTab] = useState('jour');
  const [jourMode, setJourMode] = useState('date');
  const [selectedDate, setSelectedDate] = useState(() => new Date().toISOString().split('T')[0]);
  const [dateDebut, setDateDebut] = useState('');
  const [dateFin, setDateFin] = useState('');
  const now = new Date();
  const [selectedMonth, setSelectedMonth] = useState(now.getMonth() + 1);
  const [selectedYear, setSelectedYear] = useState(() => getActiveCaisseYear());
  const years = buildCaisseYearOptions();

  const formatMontant = (montant) => {
    const v = parseFloat(montant || 0);
    return v.toLocaleString('fr-FR', {
      minimumFractionDigits: 3,
      maximumFractionDigits: 3,
    });
  };

  const handleSearch = async () => {
    setLoading(true);
    setError('');
    try {
      let url = '';
      const params = new URLSearchParams();
      // Libellé attendu côté API (crédits HONORAIRES REÇU CABINET)
      params.append('libelle', 'HONORAIRES REÇU CABINET');

      const clientId = window.currentHonorairesClientId;
      if (clientId) params.append('client_id', clientId);

      if (tab === 'jour') {
        if (jourMode === 'date') {
          if (!selectedDate) {
            setError('Choisissez une date');
            setLoading(false);
            return;
          }
          params.append('date', selectedDate);
          url = '/api/honoraires/par-jour';
        } else {
          if (!dateDebut || !dateFin) {
            setError('Choisissez une période (date début et fin)');
            setLoading(false);
            return;
          }
          params.append('date_debut', dateDebut);
          params.append('date_fin', dateFin);
          url = '/api/honoraires/par-periode';
        }
      } else if (tab === 'mois') {
        params.append('mois', String(selectedMonth).padStart(2, '0'));
        params.append('annee', String(selectedYear));
        url = '/api/honoraires/par-mois';
      } else if (tab === 'annee') {
        params.append('annee', String(selectedYear));
        url = '/api/honoraires/par-annee';
      }

      const r = await fetch(`${url}?${params.toString()}`, {
        headers: authJsonHeaders(),
      });
      const d = await r.json();
      if (d && d.success) {
        // Crédits uniquement : HONORAIRES REÇU CABINET avec montant crédit > 0
        const list = (d.honoraires || []).filter((h) => {
          const credit = parseFloat(h.avance ?? h.montant ?? 0);
          return credit > 0 && isHonoraireRecuCabinet(h.libelle);
        });
        setResults(list);
        const t = list.reduce((s, h) => s + parseFloat((h.avance ?? h.montant) || 0), 0);
        setTotal(t);
      } else {
        setResults([]);
        setTotal(0);
        setError(d?.error || 'Erreur lors du chargement');
      }
    } catch (_) {
      setResults([]);
      setTotal(0);
      setError('Erreur réseau lors du chargement');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    handleSearch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeCabinet?.id]);

  return (
    <CgmModulePageLayout
      crumb="Comptabilité clients · Honoraires"
      title="Honoraires par période"
      subtitle="Opérations crédit · HONORAIRES REÇU CABINET"
      backTo="/"
      wide
    >
      <div className="cgm-list-toolbar">
        <div className="cgm-filter-panel cgm-period-panel">
          <div className="cgm-filter-panel__title">Filtrer les honoraires reçus cabinet</div>
          <div className="cgm-period-body">
            <div className="cgm-period-tabs">
              <button type="button" className={`cgm-period-tab ${tab === 'jour' ? 'active' : ''}`} onClick={() => setTab('jour')}>Jour</button>
              <button type="button" className={`cgm-period-tab ${tab === 'mois' ? 'active' : ''}`} onClick={() => setTab('mois')}>Mois</button>
              <button type="button" className={`cgm-period-tab ${tab === 'annee' ? 'active' : ''}`} onClick={() => setTab('annee')}>Année</button>
            </div>

            {tab === 'jour' && (
              <div className="cgm-period-jour-panel">
                <div className="cgm-period-mode-group" role="group" aria-label="Mode jour">
                  <div className="form-check">
                    <input className="form-check-input" type="radio" id="honoJourDate" name="honoJourMode" checked={jourMode === 'date'} onChange={() => setJourMode('date')} />
                    <label className="form-check-label" htmlFor="honoJourDate">Par date</label>
                  </div>
                  <div className="form-check">
                    <input className="form-check-input" type="radio" id="honoJourPeriode" name="honoJourMode" checked={jourMode === 'periode'} onChange={() => setJourMode('periode')} />
                    <label className="form-check-label" htmlFor="honoJourPeriode">Par période</label>
                  </div>
                </div>
                <div className="cgm-period-filter-row">
                  {jourMode === 'date' ? (
                    <>
                      <label className="mb-0">Date</label>
                      <input type="date" className="hono-input-date" value={selectedDate} onChange={(e) => setSelectedDate(e.target.value)} />
                    </>
                  ) : (
                    <>
                      <label className="mb-0">De</label>
                      <input type="date" className="hono-input-date" value={dateDebut} onChange={(e) => setDateDebut(e.target.value)} />
                      <label className="mb-0">à</label>
                      <input type="date" className="hono-input-date" value={dateFin} onChange={(e) => setDateFin(e.target.value)} />
                    </>
                  )}
                </div>
              </div>
            )}

            {tab === 'mois' && (
              <div className="cgm-period-filter-row">
                <label className="mb-0">Mois</label>
                <select className="hono-select-month" value={selectedMonth} onChange={(e) => setSelectedMonth(parseInt(e.target.value, 10))}>
                  {Array.from({ length: 12 }, (_, i) => i + 1).map((m) => (
                    <option key={m} value={m}>{String(m).padStart(2, '0')}</option>
                  ))}
                </select>
                <label className="mb-0">Année</label>
                <select className="hono-select-year" value={selectedYear} onChange={(e) => setSelectedYear(parseInt(e.target.value, 10))}>
                  {years.map((y) => (
                    <option key={y} value={y}>{y}</option>
                  ))}
                </select>
              </div>
            )}

            {tab === 'annee' && (
              <div className="cgm-period-filter-row">
                <label className="mb-0">Année</label>
                <select className="hono-select-year" value={selectedYear} onChange={(e) => setSelectedYear(parseInt(e.target.value, 10))}>
                  {years.map((y) => (
                    <option key={y} value={y}>{y}</option>
                  ))}
                </select>
              </div>
            )}

            <div className="cgm-period-actions">
              <button type="button" className="cgm-toolbar-btn cgm-toolbar-btn--primary" onClick={handleSearch} disabled={loading}>
                {loading ? 'Recherche…' : 'Rechercher'}
              </button>
            </div>
          </div>
        </div>
      </div>

      {error && (
        <div className="alert alert-danger" role="alert">{error}</div>
      )}

      {loading && (
        <div className="text-center my-4">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Chargement…</span>
          </div>
        </div>
      )}

      {!loading && results.length > 0 && (
        <div className="nh-modern-table-card">
          <div className="nh-modern-table-toolbar">
            <div className="nh-modern-table-info">
              {results.length} honoraire{results.length > 1 ? 's' : ''} · Total{' '}
              <strong>{formatMontant(total)}</strong>
            </div>
          </div>
          <div className="overview-table-container nh-modern-table-scroll">
            <table className="table overview-table nh-overview-data-table nh-modern-data-table">
              <thead>
                <tr>
                  <th><span className="nh-modern-th-inner">Date</span></th>
                  <th><span className="nh-modern-th-inner">Client</span></th>
                  <th><span className="nh-modern-th-inner">Libellé</span></th>
                  <th><span className="nh-modern-th-inner">Montant</span></th>
                </tr>
              </thead>
              <tbody>
                {results.map((h, idx) => (
                  <tr key={h.id || idx}>
                    <td>{h.date ? new Date(h.date).toLocaleDateString('fr-FR') : '—'}</td>
                    <td>{(h.client_nom || '') + (h.client_prenom ? ` ${h.client_prenom}` : '')}</td>
                    <td>{formatLibelleForDisplay(h.libelle || 'HONORAIRES REÇU CABINET')}</td>
                    <td className="text-end">
                      <span className="nh-badge-amount" style={{ color: '#059669' }}>
                        {formatMontant(h.avance ?? h.montant ?? 0)}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr>
                  <td colSpan={3}><strong>TOTAL</strong></td>
                  <td className="text-end"><strong style={{ color: '#059669' }}>{formatMontant(total)}</strong></td>
                </tr>
              </tfoot>
            </table>
          </div>
        </div>
      )}

      {!loading && !error && results.length === 0 && (
        <div className="text-center text-muted py-4">
          Aucun crédit « HONORAIRES REÇU CABINET » pour cette période.
        </div>
      )}
    </CgmModulePageLayout>
  );
}
